import type { RefObject } from 'react';

import { useEffect } from 'react';

/**
 * Calls the callback function when clicked outside the element passed as ref.
 * You can pass `data-ignore-click-outside` attribute to the element to ignore the click checks.
 * @param {RefObject<HTMLElement | null>} ref The element to check for clicks outside.
 * @param {() => void} callback The callback function to be executed when clicked outside the element.
 * @param {(Element | null)[]} ignoreElements An array of elements to ignore when checking for clicks outside.
 *
 */
export function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  callback: () => void,
  ignoreElements = [] as (Element | null)[]
) {
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement;

      if (
        !ref.current ||
        ref.current.contains(clickedElement) ||
        ignoreElements.some((element) => element?.contains(clickedElement)) ||
        clickedElement.getAttribute('data-ignore-click-outside') === 'true'
      ) {
        return;
      }

      callback();
    };

    document.addEventListener('mousedown', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
    };
  }, [ref, callback]);
}
